import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { Checkbox } from '../../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Package,
  Clock,
  CheckCircle,
  AlertCircle,
  Truck,
  FileText,
  Eye,
  Download,
  Save
} from 'lucide-react';
import biogasService, { ProductionBatch, CreateBatchRequest, CompleteBatchRequest, DungCollectionResponse, ClusterResponse } from '../../services/biogasService';
import api from '../../services/api';

// Mock types for BatchManagement (will be replaced with backend types)
type BatchStatus = 'processing' | 'ready' | 'transferred' | 'rejected' | 'testing' | 'SCHEDULED' | 'IN_PRODUCTION' | 'COMPLETED' | 'QUALITY_TESTED' | 'READY_FOR_SALE';

interface BatchData {
  id: string;
  digesterId: string;
  volume: number;
  methaneContent: number;
  status: BatchStatus;
  createdDate: string;
  transferDate?: string;
  qualityScore?: number;
  notes: string;
  farmerName?: string;
  source?: string;
  operatorUserId?: string;
}

interface QualityCertificate {
  id: string;
  batchId: string;
  issuedDate: string;
  qualityScore: number;
  certifiedBy?: string;
  validUntil?: string;
}

// Enhanced Mock data with realistic Indian biogas operations
const batches: BatchData[] = [
  {
    id: 'BATCH-001',
    digesterId: 'DIG-001',
    volume: 45.2,
    methaneContent: 65.8,
    status: 'ready',
    createdDate: new Date().toISOString(),
    transferDate: new Date(Date.now() + 7200000).toISOString(),
    qualityScore: 92,
    farmerName: 'राजेश कुमार',
    source: 'Gokul Gaushala, Mathura',
    notes: 'Premium quality cow dung batch from Rajesh Kumar. Optimal fermentation achieved with excellent gas composition. Ready for immediate transfer to distribution network. Source: 15 indigenous cows, organic feed only.'
  },
  {
    id: 'BATCH-002',
    digesterId: 'DIG-002',
    volume: 38.7,
    methaneContent: 62.3,
    status: 'processing',
    createdDate: new Date(Date.now() - 86400000).toISOString(),
    qualityScore: 78,
    farmerName: 'सुनीता देवी',
    source: 'Vrindavan Organic Farm',
    notes: 'Mixed dung batch in active fermentation phase. Temperature maintained at 37°C. Expected completion in 48 hours with good quality prospects. Contains cow and buffalo dung mixture from certified organic farm.'
  },
  {
    id: 'BATCH-003',
    digesterId: 'DIG-003',
    volume: 52.8,
    methaneContent: 68.2,
    status: 'ready',
    createdDate: new Date(Date.now() - 172800000).toISOString(),
    transferDate: new Date().toISOString(),
    qualityScore: 95,
    farmerName: 'श्याम यादव',
    source: 'Yamuna Riverside Gaushala',
    notes: 'Exceptional buffalo dung batch from Shyam Yadav. Highest methane content achieved this quarter. Certified Grade A premium quality. Source: 25 Murrah buffaloes with optimal nutrition and care.'
  },
  {
    id: 'BATCH-004',
    digesterId: 'DIG-001',
    volume: 41.3,
    methaneContent: 58.9,
    status: 'testing',
    createdDate: new Date(Date.now() - 259200000).toISOString(),
    qualityScore: 72,
    farmerName: 'अमित शर्मा',
    source: 'Govind Dairy Farm',
    notes: 'Standard cow dung batch undergoing final quality assessment. Moderate methane levels detected. Awaiting lab confirmation for Grade B classification. Processing time extended due to lower initial moisture content.'
  },
  {
    id: 'BATCH-005',
    digesterId: 'DIG-004',
    volume: 29.6,
    methaneContent: 55.4,
    status: 'rejected',
    createdDate: new Date(Date.now() - 345600000).toISOString(),
    qualityScore: 48,
    farmerName: 'प्रेम सिंह',
    source: 'Village Cooperative',
    notes: 'Batch from contaminated feedstock. Low methane content due to improper storage conditions. Rejected for commercial use, diverted to compost production. Root cause: contamination from non-organic sources and poor collection hygiene.'
  },
  {
    id: 'BATCH-006',
    digesterId: 'DIG-002',
    volume: 47.9,
    methaneContent: 63.7,
    status: 'transferred',
    createdDate: new Date(Date.now() - 432000000).toISOString(),
    transferDate: new Date(Date.now() - 86400000).toISOString(),
    qualityScore: 86,
    farmerName: 'गीता देवी',
    source: 'Krishna Valley Farm',
    notes: 'High-quality mixed dung batch successfully transferred to Mathura distribution center. Excellent fermentation results with consistent gas output. Customer feedback: exceptional performance in commercial biogas units.'
  },
  {
    id: 'BATCH-007',
    digesterId: 'DIG-003',
    volume: 35.1,
    methaneContent: 71.2,
    status: 'ready',
    createdDate: new Date(Date.now() - 518400000).toISOString(),
    qualityScore: 97,
    farmerName: 'राम प्रसाद',
    source: 'Organic Gaushala Trust',
    notes: 'Premium buffalo dung batch with exceptional methane purity. Sourced from Organic Gaushala cooperative. Meets all export quality standards. Tested for international markets - approved for European biogas facilities.'
  },
  {
    id: 'BATCH-008',
    digesterId: 'DIG-005',
    volume: 33.4,
    methaneContent: 59.8,
    status: 'processing',
    createdDate: new Date(Date.now() - 604800000).toISOString(),
    qualityScore: 74,
    farmerName: 'लक्ष्मी यादव',
    source: 'Braj Bhumi Gaushala',
    notes: 'Cow dung batch from Gita Devi\'s farm. Processing delayed due to monsoon humidity. Temperature regulation implemented, expecting improvement. Additional heating systems deployed to counter seasonal moisture challenges.'
  },
  {
    id: 'BATCH-009',
    digesterId: 'DIG-001',
    volume: 44.7,
    methaneContent: 66.4,
    status: 'testing',
    createdDate: new Date(Date.now() - 691200000).toISOString(),
    qualityScore: 88,
    farmerName: 'विकास कुमार',
    source: 'Vrindavan Heritage Gaushala',
    notes: 'Large volume cow dung batch from Vrindavan Gaushala. Final quality checks in progress. Preliminary results show excellent commercial viability. Volume sufficient for pilot testing in urban biogas distribution network.'
  },
  {
    id: 'BATCH-010',
    digesterId: 'DIG-004',
    volume: 39.8,
    methaneContent: 64.1,
    status: 'transferred',
    createdDate: new Date(Date.now() - 777600000).toISOString(),
    transferDate: new Date(Date.now() - 259200000).toISOString(),
    qualityScore: 83,
    farmerName: 'सुरेश चंद्र',
    source: 'Radha Krishna Dairy',
    notes: 'Standard quality mixed dung batch successfully delivered to regional distribution network. Good consistency maintained throughout fermentation cycle. Successfully integrated into 5 commercial biogas plants in the region.'
  }
];

const certificates: QualityCertificate[] = [
  {
    id: 'CERT-001',
    batchId: 'BATCH-001',
    issuedDate: new Date().toISOString(),
    qualityScore: 92,
    certifiedBy: 'Dr. Ravi Sharma, Quality Assurance',
    validUntil: new Date(Date.now() + 15552000000).toISOString() // 6 months validity
  },
  {
    id: 'CERT-002',
    batchId: 'BATCH-003',
    issuedDate: new Date(Date.now() - 86400000).toISOString(),
    qualityScore: 95,
    certifiedBy: 'Dr. Priya Gupta, Chief Quality Officer',
    validUntil: new Date(Date.now() + 15465600000).toISOString()
  },
  {
    id: 'CERT-003',
    batchId: 'BATCH-006',
    issuedDate: new Date(Date.now() - 172800000).toISOString(),
    qualityScore: 86,
    certifiedBy: 'Dr. Anil Kumar, Senior Analyst',
    validUntil: new Date(Date.now() + 15379200000).toISOString()
  },
  {
    id: 'CERT-004',
    batchId: 'BATCH-007',
    issuedDate: new Date(Date.now() - 259200000).toISOString(),
    qualityScore: 97,
    certifiedBy: 'Dr. Sunita Singh, Export Certification',
    validUntil: new Date(Date.now() + 15292800000).toISOString()
  },
  {
    id: 'CERT-005',
    batchId: 'BATCH-009',
    issuedDate: new Date(Date.now() - 345600000).toISOString(),
    qualityScore: 88,
    certifiedBy: 'Dr. Manoj Verma, Quality Assurance',
    validUntil: new Date(Date.now() + 15206400000).toISOString()
  },
  {
    id: 'CERT-006',
    batchId: 'BATCH-010',
    issuedDate: new Date(Date.now() - 432000000).toISOString(),
    qualityScore: 83,
    certifiedBy: 'Dr. Deepak Mishra, Regional Quality Head',
    validUntil: new Date(Date.now() + 15120000000).toISOString()
  }
];

// Mock functions
const createBatch = async (data: Partial<BatchData>) => {
  console.log('Creating batch:', data);
  return Promise.resolve();
};

const updateBatch = async (id: string, data: Partial<BatchData>) => {
  console.log('Updating batch:', id, data);
  return Promise.resolve();
};

const deleteBatch = async (id: string) => {
  console.log('Deleting batch:', id);
  return Promise.resolve();
};

interface BatchManagementProps {
  languageContext?: {
    language: 'hi' | 'en';
    t: (key: string) => string;
  };
}

const translations = {
  en: {
    title: 'Batch Management',
    subtitle: 'Manage biogas batches from production to transfer',
    createBatch: 'Create New Batch',
    editBatch: 'Edit Batch',
    deleteBatch: 'Delete Batch',
    batchList: 'Batch List',
    activeQualities: 'Active Quality Tests',
    certificates: 'Quality Certificates',
    search: 'Search batches...',
    filter: 'Filter',
    batchId: 'Batch ID',
    digesterId: 'Digester ID',
    volume: 'Volume (m³)',
    methaneContent: 'Methane Content (%)',
    status: 'Status',
    createdDate: 'Created Date',
    transferDate: 'Transfer Date',
    qualityScore: 'Quality Score',
    notes: 'Notes',
    actions: 'Actions',
    create: 'Create',
    update: 'Update',
    cancel: 'Cancel',
    delete: 'Delete',
    confirm: 'Confirm',
    processing: 'Processing',
    ready: 'Ready',
    transferred: 'Transferred',
    rejected: 'Rejected',
    testing: 'Testing',
    viewDetails: 'View Details',
    downloadCert: 'Download Certificate',
    transferBatch: 'Transfer Batch',
    qualityTest: 'Quality Test',
    batchDetails: 'Batch Details',
    transferHistory: 'Transfer History',
    qualityReports: 'Quality Reports',
    farmerName: 'Farmer Name',
    source: 'Source',
    certifiedBy: 'Certified By',
    validUntil: 'Valid Until'
  },
  hi: {
    title: 'बैच प्रबंधन',
    subtitle: 'उत्पादन से स्थानांतरण तक बायोगैस बैचों का प्रबंधन',
    createBatch: 'नया बैच बनाएं',
    editBatch: 'बैच संपादित करें',
    deleteBatch: 'बैच हटाएं',
    batchList: 'बैच सूची',
    activeQualities: 'सक्रिय गुणवत्ता परीक्षण',
    certificates: 'गुणवत्ता प्रमाणपत्र',
    search: 'बैच खोजें...',
    filter: 'फिल्टर',
    batchId: 'बैच आईडी',
    digesterId: 'डाइजेस्टर आईडी',
    volume: 'आयतन (m³)',
    methaneContent: 'मीथेन सामग्री (%)',
    status: 'स्थिति',
    createdDate: 'निर्मित दिनांक',
    transferDate: 'स्थानांतरण दिनांक',
    qualityScore: 'गुणवत्ता स्कोर',
    notes: 'टिप्पणियां',
    actions: 'कार्य',
    create: 'बनाएं',
    update: 'अपडेट करें',
    cancel: 'रद्द करें',
    delete: 'हटाएं',
    confirm: 'पुष्टि करें',
    processing: 'प्रसंस्करण',
    ready: 'तैयार',
    transferred: 'स्थानांतरित',
    rejected: 'अस्वीकृत',
    testing: 'परीक्षण',
    viewDetails: 'विवरण देखें',
    downloadCert: 'प्रमाणपत्र डाउनलोड करें',
    transferBatch: 'बैच स्थानांतरित करें',
    qualityTest: 'गुणवत्ता परीक्षण',
    batchDetails: 'बैच विवरण',
    transferHistory: 'स्थानांतरण इतिहास',
    qualityReports: 'गुणवत्ता रिपोर्ट',
    farmerName: 'किसान का नाम',
    source: 'स्रोत',
    certifiedBy: 'प्रमाणित करने वाला',
    validUntil: 'तक वैध'
  }
};

const getStatusColor = (status: BatchStatus): string => {
  switch (status) {
    case 'processing':
      return 'bg-blue-100 text-blue-800';
    case 'ready':
      return 'bg-green-100 text-green-800';
    case 'transferred':
      return 'bg-gray-100 text-gray-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    case 'testing':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status: BatchStatus) => {
  switch (status) {
    case 'processing':
    case 'IN_PRODUCTION':
      return <Clock className="w-4 h-4" />;
    case 'ready':
    case 'READY_FOR_SALE':
      return <CheckCircle className="w-4 h-4" />;
    case 'transferred':
    case 'COMPLETED':
      return <Truck className="w-4 h-4" />;
    case 'rejected':
      return <AlertCircle className="w-4 h-4" />;
    case 'testing':
    case 'QUALITY_TESTED':
      return <Package className="w-4 h-4" />;
    case 'SCHEDULED':
      return <Clock className="w-4 h-4" />;
    default:
      return <Package className="w-4 h-4" />;
  }
};

// Helper function to map backend batch status to frontend
const mapBackendStatusToFrontend = (backendStatus: string): BatchStatus => {
  switch (backendStatus) {
    case 'SCHEDULED':
      return 'testing';
    case 'IN_PRODUCTION':
      return 'processing';
    case 'COMPLETED':
      return 'transferred';
    case 'QUALITY_TESTED':
      return 'testing';
    case 'READY_FOR_SALE':
      return 'ready';
    default:
      return 'processing';
  }
};

// Helper function to calculate quality score from batch data
const calculateQualityScore = (batch: ProductionBatch): number => {
  if (!batch.methaneContentPercent) return 0;

  // Simple quality calculation based on methane content and efficiency
  const methaneScore = (batch.methaneContentPercent / 70) * 60; // Max 60 points
  const efficiencyScore = ((batch.productionEfficiencyPercent || 0) / 100) * 40; // Max 40 points

  return Math.min(100, Math.round(methaneScore + efficiencyScore));
};

const BatchForm: React.FC<{
  batch?: BatchData;
  onSubmit: (data: Partial<BatchData>) => Promise<void> | void;
  onCancel: () => void;
  onRefreshDungCollections?: () => Promise<void>; // ✅ New callback to refresh dropdown
  t: (key: string) => string;
}> = ({ batch, onSubmit, onCancel, onRefreshDungCollections, t }) => {
  const isEditMode = !!batch; // Determine if we're editing an existing batch
  
  // Helper function to map frontend status back to backend status
  const mapFrontendStatusToBackend = (frontendStatus: BatchStatus): 'SCHEDULED' | 'IN_PRODUCTION' | 'COMPLETED' | 'QUALITY_TESTED' | 'READY_FOR_SALE' => {
    switch (frontendStatus) {
      case 'testing':
        return 'QUALITY_TESTED';
      case 'processing':
        return 'IN_PRODUCTION';
      case 'transferred':
        return 'COMPLETED';
      case 'ready':
        return 'READY_FOR_SALE';
      case 'SCHEDULED':
      case 'IN_PRODUCTION':
      case 'COMPLETED':
      case 'QUALITY_TESTED':
      case 'READY_FOR_SALE':
        return frontendStatus;
      default:
        return 'SCHEDULED';
    }
  };

  // Get backend status from batch if available
  const getBackendStatus = (): 'SCHEDULED' | 'IN_PRODUCTION' | 'COMPLETED' | 'QUALITY_TESTED' | 'READY_FOR_SALE' => {
    if (batch?.status) {
      // Check if it's already a backend status
      if (['SCHEDULED', 'IN_PRODUCTION', 'COMPLETED', 'QUALITY_TESTED', 'READY_FOR_SALE'].includes(batch.status)) {
        return batch.status as 'SCHEDULED' | 'IN_PRODUCTION' | 'COMPLETED' | 'QUALITY_TESTED' | 'READY_FOR_SALE';
      }
      // Otherwise map from frontend status
      return mapFrontendStatusToBackend(batch.status);
    }
    return 'SCHEDULED';
  };

  const [formData, setFormData] = useState({
    digesterId: batch?.digesterId || '',
    notes: batch?.notes || '',
    batchStatus: getBackendStatus(),
    operatorUserId: batch?.operatorUserId || '',
    dungCollectionIds: [] as string[]
  });
  const [isSaving, setIsSaving] = useState(false);
  const [dungCollections, setDungCollections] = useState<DungCollectionResponse[]>([]);
  const [loadingDungCollections, setLoadingDungCollections] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [userClusters, setUserClusters] = useState<ClusterResponse[]>([]);
  const [loadingClusters, setLoadingClusters] = useState(false);

  // Load user's accessible clusters when component mounts
  useEffect(() => {
    const loadUserClusters = async () => {
      setLoadingClusters(true);
      try {
        const response = await biogasService.getMyAccessibleClusters();
        if (response.success && response.data) {
          setUserClusters(response.data);
          // Auto-select first cluster if creating new batch and no cluster selected
          if (!batch && response.data.length > 0 && !formData.digesterId) {
            setFormData(prev => ({ ...prev, digesterId: response.data[0].id.toString() }));
          }
        }
      } catch (error) {
        console.error('Error loading user clusters:', error);
      } finally {
        setLoadingClusters(false);
      }
    };
    loadUserClusters();
  }, [batch]);

  // Load dung collections when component mounts
  useEffect(() => {
    const loadDungCollections = async () => {
      setLoadingDungCollections(true);
      try {
        const response = await biogasService.listDungCollections(
          undefined, // clusterId
          undefined, // gaushalaId
          undefined, // paymentStatus
          undefined, // startDate
          undefined, // endDate
          0, // page
          100 // size
        );
        if (response.success && response.data) {
          setDungCollections(response.data.content || []);
        }
      } catch (error) {
        console.error('Error loading dung collections:', error);
      } finally {
        setLoadingDungCollections(false);
      }
    };
    loadDungCollections();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      // Pass both formData and dungCollections to parent's handler
      await onSubmit({ ...formData, _dungCollections: dungCollections });

      // ✅ Auto-refresh the dung collections dropdown after successful submission
      if (onRefreshDungCollections) {
        await onRefreshDungCollections();
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate totals from selected collections - memoized for performance
  const totals = useMemo(() => {
    const selectedCollections = dungCollections.filter(c =>
      formData.dungCollectionIds.includes(c.id?.toString() || '')
    );

    const totalWeight = selectedCollections.reduce((sum, c) => sum + (c.weightKg || 0), 0);
    const totalAmount = selectedCollections.reduce((sum, c) => sum + (c.totalAmount || 0), 0);
    const sources = [...new Set(selectedCollections.map(c => `Gaushala ${c.gaushalaId}`))].join(', ');

    return { totalWeight, totalAmount, sources, count: selectedCollections.length };
  }, [dungCollections, formData.dungCollectionIds]);

  // Handle checkbox change
  const handleCollectionToggle = (collectionId: string) => {
    setFormData(prev => {
      const newIds = prev.dungCollectionIds.includes(collectionId)
        ? prev.dungCollectionIds.filter(id => id !== collectionId)
        : [...prev.dungCollectionIds, collectionId];

      return {
        ...prev,
        dungCollectionIds: newIds
      };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Only show dung collection selection when creating a new batch */}
      {!isEditMode && (
        <div className="space-y-2">
          <Label htmlFor="dungCollectionIds">Dung Transaction Records (Multi-Select)</Label>
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={isPopoverOpen}
                className="w-full justify-between"
                type="button"
                disabled={loadingDungCollections}
              >
                {loadingDungCollections ? (
                  "Loading dung collections..."
                ) : formData.dungCollectionIds.length === 0 ? (
                  "Select dung transaction records"
                ) : (
                  `${formData.dungCollectionIds.length} transaction(s) selected`
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[600px] p-0">
              <div className="max-h-[400px] overflow-y-auto p-4">
                {/* Filter to show only unbatched transactions */}
                {dungCollections.filter(c => !c.assignedToBatch).length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    {dungCollections.length === 0 ? 'No dung collections available' : 'All transactions have been assigned to batches'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dungCollections
                      .filter(c => !c.assignedToBatch) // ✅ Only show unbatched transactions
                      .map((collection) => (
                      <div
                        key={collection.id}
                        className="flex items-start space-x-3 p-2 hover:bg-muted rounded-lg"
                      >
                        <Checkbox
                          checked={formData.dungCollectionIds.includes(collection.id?.toString() || '')}
                          onCheckedChange={() => handleCollectionToggle(collection.id?.toString() || '')}
                          className="mt-1"
                        />
                        <div className="flex-1 space-y-1">
                          <div className="font-medium text-sm">
                            {collection.transactionRef}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {collection.weightKg}kg • {new Date(collection.collectionDate).toLocaleDateString()} • Grade {collection.qualityGrade} • ₹{collection.totalAmount}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Gaushala {collection.gaushalaId}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {formData.dungCollectionIds.length > 0 && (
            <div className="text-sm text-muted-foreground mt-2 p-3 bg-muted rounded-lg">
              <strong>Selected Collections Summary:</strong>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <span className="font-medium">Total Transactions:</span> {totals.count}
                </div>
                <div>
                  <span className="font-medium">Total Weight:</span> {totals.totalWeight} kg
                </div>
                <div>
                  <span className="font-medium">Total Amount:</span> ₹{totals.totalAmount}
                </div>
                <div>
                  <span className="font-medium">Sources:</span> {totals.sources}
                </div>
              </div>
              <div className="mt-3 space-y-1">
                <strong>Selected Transactions:</strong>
                {dungCollections
                  .filter(c => formData.dungCollectionIds.includes(c.id?.toString() || ''))
                  .map(c => (
                    <div key={c.id} className="text-xs pl-2 border-l-2 border-primary ml-2 py-1">
                      <div><strong>{c.transactionRef}</strong> - Gaushala {c.gaushalaId}</div>
                      <div>Weight: {c.weightKg}kg • Grade {c.qualityGrade} • Amount: ₹{c.totalAmount}</div>
                      <div>Date: {new Date(c.collectionDate).toLocaleString()}</div>
                      {c.qualityNotes && <div>Notes: {c.qualityNotes}</div>}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Only show cluster selection when creating a new batch */}
      {!isEditMode && (
        <div className="space-y-2">
          <Label htmlFor="digesterId">Cluster / Biogas Plant *</Label>
          <Select
            value={formData.digesterId}
            onValueChange={(value) => setFormData(prev => ({ ...prev, digesterId: value }))}
            disabled={loadingClusters}
          >
            <SelectTrigger>
              <SelectValue placeholder={loadingClusters ? "Loading clusters..." : "Select cluster"} />
            </SelectTrigger>
            <SelectContent>
              {userClusters.length === 0 && !loadingClusters ? (
                <SelectItem value="no-access" disabled>No clusters available</SelectItem>
              ) : (
                userClusters.map((cluster) => (
                  <SelectItem key={cluster.id} value={cluster.id.toString()}>
                    {cluster.clusterName} ({cluster.clusterCode})
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {loadingClusters && (
            <p className="text-xs text-muted-foreground">Loading your accessible clusters...</p>
          )}
          {!loadingClusters && userClusters.length === 0 && (
            <p className="text-xs text-destructive">No clusters assigned. Please contact administrator.</p>
          )}
        </div>
      )}



      {/* Show batch status selector when editing */}
      {isEditMode && (
        <div className="space-y-2">
          <Label htmlFor="batchStatus">Batch Status</Label>
          <Select
            value={formData.batchStatus}
            onValueChange={(value) => setFormData(prev => ({ ...prev, batchStatus: value as BatchStatus }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="SCHEDULED">Scheduled</SelectItem>
              <SelectItem value="IN_PRODUCTION">In Production</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="QUALITY_TESTED">Quality Tested</SelectItem>
              <SelectItem value="READY_FOR_SALE">Ready for Sale</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Show operator user ID field when editing */}
      {isEditMode && (
        <div className="space-y-2">
          <Label htmlFor="operatorUserId">Operator User ID</Label>
          <Input
            id="operatorUserId"
            value={formData.operatorUserId}
            onChange={(e) => setFormData(prev => ({ ...prev, operatorUserId: e.target.value }))}
            placeholder="Enter operator user ID or phone number"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="notes">{t('notes')}</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          placeholder="Detailed batch information including source details, quality parameters, and special notes..."
          rows={4}
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          className="gap-2"
          disabled={isSaving || (!isEditMode && formData.dungCollectionIds.length === 0)}
          title={(!isEditMode && formData.dungCollectionIds.length === 0) ? "Please select at least one dung transaction record" : ""}
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : (batch ? t('update') : 'Save Batch')}
        </Button>
      </DialogFooter>

      {!isEditMode && formData.dungCollectionIds.length === 0 && (
        <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          ⚠️ Please select at least one dung transaction record to create a batch.
        </div>
      )}
    </form>
  );
};

export const BatchManagement: React.FC<BatchManagementProps> = ({ languageContext }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<BatchStatus | 'all'>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState<BatchData | null>(null);
  const [deletingBatch, setDeletingBatch] = useState<BatchData | null>(null);
  const [viewingBatch, setViewingBatch] = useState<BatchData | null>(null);
  const [viewingProgress, setViewingProgress] = useState<BatchData | null>(null);

  // Backend integration state
  const [backendBatches, setBackendBatches] = useState<ProductionBatch[]>([]);
  const [loading, setLoading] = useState(false); // Start as false, will be set to true when fetching starts
  const [error, setError] = useState<string | null>(null);
  const [userClusters, setUserClusters] = useState<ClusterResponse[]>([]);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [dungCollections, setDungCollections] = useState<DungCollectionResponse[]>([]);
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>('all'); // Default to "all" to show all batches
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Counter to trigger batch refresh

  const lang = languageContext?.language || 'en';
  const t = (key: string): string => {
    return languageContext?.t(key) || translations[lang][key as keyof typeof translations[typeof lang]] || key;
  };

  // Load user's accessible clusters when component mounts
  useEffect(() => {
    const loadUserClusters = async () => {
      setLoadingClusters(true);
      try {
        const response = await biogasService.getMyAccessibleClusters();
        if (response.success && response.data) {
          setUserClusters(response.data);
          // Default to "all" to show batches for all clusters the user has access to
          // User can then select a specific cluster if needed
        }
      } catch (error) {
        console.error('Error loading user clusters:', error);
      } finally {
        setLoadingClusters(false);
      }
    };
    loadUserClusters();
  }, []);

  // Fetch batches from backend - whenever selected cluster changes
  useEffect(() => {
    const fetchBatches = async () => {
      // Don't fetch if clusters haven't loaded yet
      if (loadingClusters) {
        console.log('⏳ [BatchManagement] Waiting for clusters to load...');
        return;
      }

      if (userClusters.length === 0) {
        console.log('⚠️ [BatchManagement] No clusters available for user');
        setLoading(false);
        setBackendBatches([]);
        setError('No clusters assigned. Please contact administrator.');
        return;
      }

      console.log(`🔄 [BatchManagement] Starting batch fetch - selectedClusterId: ${selectedClusterId}, userClusters: ${userClusters.length}`);
      setLoading(true);
      setError(null);

      try {
        if (selectedClusterId === 'all' || !selectedClusterId) {
          // Fetch batches for ALL clusters the user has access to
          // Since backend doesn't support filtering by unitId, we fetch batches for each cluster
          console.log(`🔄 [BatchManagement] Fetching batches for ALL ${userClusters.length} clusters user has access to...`);
          
          const allBatches: ProductionBatch[] = [];
          const errors: string[] = [];

          // Fetch batches for each cluster the user has access to
          for (const cluster of userClusters) {
            // Validate cluster has an ID
            if (!cluster.id) {
              console.warn(`⚠️ [BatchManagement] Cluster ${cluster.clusterName} has no ID, skipping...`);
              errors.push(`Cluster ${cluster.clusterName} has no valid ID`);
              continue;
            }

            try {
              const clusterIdStr = cluster.id.toString();
              console.log(`📡 [BatchManagement] Calling API for cluster ${cluster.clusterName} (ID: ${clusterIdStr})`);
              const response = await biogasService.getBatches(clusterIdStr, undefined, 0, 50);
              
              console.log(`📥 [BatchManagement] Response for cluster ${cluster.clusterName}:`, {
                success: response.success,
                hasData: !!response.data,
                hasContent: !!response.data?.content,
                contentLength: response.data?.content?.length || 0,
                error: response.error
              });

              if (response.success && response.data?.content) {
                allBatches.push(...response.data.content);
                console.log(`✅ [BatchManagement] Fetched ${response.data.content.length} batches for cluster ${cluster.clusterName}`);
              } else {
                const errorMsg = response.error || 'Unknown error';
                console.error(`❌ [BatchManagement] Failed to fetch batches for cluster ${cluster.clusterName}:`, errorMsg);
                errors.push(`Failed to fetch batches for cluster ${cluster.clusterName}: ${errorMsg}`);
              }
            } catch (err) {
              const errorMsg = err instanceof Error ? err.message : 'Unknown error';
              console.error(`❌ [BatchManagement] Exception fetching batches for cluster ${cluster.clusterName}:`, err);
              errors.push(`Error fetching batches for cluster ${cluster.clusterName}: ${errorMsg}`);
            }
          }

          // Remove duplicates (in case same batch appears in multiple clusters - shouldn't happen but just in case)
          const uniqueBatches = Array.from(
            new Map(allBatches.map(batch => [batch.id || batch.batchNumber, batch])).values()
          );

          // Sort by creation date (newest first)
          uniqueBatches.sort((a, b) => {
            const dateA = new Date(a.productionStartDate || a.createdAt || 0).getTime();
            const dateB = new Date(b.productionStartDate || b.createdAt || 0).getTime();
            return dateB - dateA;
          });

          setBackendBatches(uniqueBatches);
          setError(errors.length > 0 ? errors.join('; ') : null);
          console.log(`✅ [BatchManagement] Total batches fetched: ${uniqueBatches.length} from ${userClusters.length} clusters`);
          
          if (errors.length > 0) {
            console.warn(`⚠️ [BatchManagement] Some errors occurred:`, errors);
          }
        } else {
          // Fetch batches for the selected cluster only
          // Validate cluster ID
          if (!selectedClusterId || selectedClusterId === '') {
            console.error(`❌ [BatchManagement] Invalid cluster ID: ${selectedClusterId}`);
            setError('Invalid cluster selected');
            setBackendBatches([]);
            setLoading(false);
            return;
          }

          console.log(`🔄 [BatchManagement] Fetching batches for specific cluster ID: ${selectedClusterId}`);
          const response = await biogasService.getBatches(selectedClusterId, undefined, 0, 50);
          
          console.log(`📥 [BatchManagement] Response for cluster ${selectedClusterId}:`, {
            success: response.success,
            hasData: !!response.data,
            hasContent: !!response.data?.content,
            contentLength: response.data?.content?.length || 0,
            error: response.error
          });

          if (response.success && response.data) {
            setBackendBatches(response.data.content || []);
            setError(null);
            console.log(`✅ [BatchManagement] Fetched ${response.data.content?.length || 0} batches for cluster ${selectedClusterId}`);
          } else {
            const errorMsg = response.error || 'Failed to fetch batches';
            console.error(`❌ [BatchManagement] Failed to fetch batches:`, errorMsg);
            setError(errorMsg);
            setBackendBatches([]);
          }
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Network error while fetching batches';
        console.error(`❌ [BatchManagement] Exception in fetchBatches:`, err);
        setError(errorMsg);
        setBackendBatches([]);
      } finally {
        setLoading(false);
        console.log(`🏁 [BatchManagement] Batch fetch completed. Loading: false`);
      }
    };

    fetchBatches();
  }, [selectedClusterId, userClusters, loadingClusters, refreshTrigger]);

  // Map backend batches to frontend format
  const mappedBatches: BatchData[] = backendBatches.map(batch => ({
    id: batch.batchNumber,
    digesterId: `DIG-${batch.id}`,
    volume: batch.biogasProducedCubicMeters || 0,
    methaneContent: batch.methaneContentPercent || 0,
    status: mapBackendStatusToFrontend(batch.batchStatus),
    createdDate: batch.productionStartDate,
    transferDate: batch.productionEndDate,
    qualityScore: calculateQualityScore(batch),
    farmerName: 'Farmer', // TODO: Get from contributions
    source: `Cluster ${batch.clusterId}`,
    notes: batch.notes || '',
    operatorUserId: batch.operatorUserId || ''
  }));

  // Use mapped batches or fallback to mock data
  const batchesToDisplay = backendBatches.length > 0 ? mappedBatches : batches;

  const filteredBatches = batchesToDisplay.filter(batch => {
    const matchesSearch = batch.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         batch.digesterId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         batch.farmerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         batch.source?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || batch.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleCreateBatch = async (data: Partial<BatchData> & { dungCollectionIds?: string[], _dungCollections?: DungCollectionResponse[] }) => {
    try {
      // Use dungCollections passed from form, or fall back to parent's state
      const collectionsList = data._dungCollections || dungCollections;

      // Calculate total weight from selected dung collections
      const selectedCollections = collectionsList.filter(c =>
        data.dungCollectionIds?.includes(c.id?.toString() || '')
      );
      const totalInputWeightKg = selectedCollections.reduce((sum, c) => sum + (c.weightKg || 0), 0);

      // Format date as LocalDateTime: YYYY-MM-DDTHH:mm:ss (no milliseconds or timezone)
      // Backend expects LocalDateTime format without 'Z' timezone indicator
      const now = new Date();
      const formattedDate = now.toISOString().split('.')[0]; // Remove milliseconds and timezone

      const request: CreateBatchRequest = {
        clusterId: parseInt(data.digesterId || '0', 10),  // ✅ Convert string to number (Long)
        sourceContributions: [],
        dungIds: data.dungCollectionIds || [],
        totalInputWeightKg: totalInputWeightKg,
        productionStartDate: formattedDate, // YYYY-MM-DDTHH:mm:ss format for LocalDateTime
        notes: data.notes
      };

      console.log('📤 [BatchManagement] Creating batch with request:', JSON.stringify(request, null, 2));

      const response = await biogasService.createBatch(request);
      console.log('📥 [BatchManagement] Create batch response:', response);
      
      if (response.success) {
        console.log('✅ [BatchManagement] Batch created successfully:', response.data);
        setIsCreateDialogOpen(false);
        // Show success toast
        const toast = await import('sonner');
        toast.toast.success('Batch Saved Successfully!', {
          description: `Batch ${response.data?.batchNumber || 'created'} created successfully`
        });

        // ✅ Update dung collections to mark selected ones as assigned
        setDungCollections(prev => prev.map(collection => ({
          ...collection,
          assignedToBatch: collection.id ? data.dungCollectionIds?.includes(collection.id?.toString() || '') ? true : collection.assignedToBatch : collection.assignedToBatch
        })));

        // Refresh batches - trigger re-fetch by incrementing refresh counter
        setRefreshTrigger(prev => prev + 1);
      } else {
        const errorMsg = response.error || 'Failed to create batch';
        console.error('❌ [BatchManagement] Failed to create batch:', errorMsg);
        setError(errorMsg);
        const toast = await import('sonner');
        toast.toast.error('Failed to Save Batch', {
          description: errorMsg
        });
      }
    } catch (err) {
      console.error('Error creating batch:', err);
      const errorMsg = 'Network error while creating batch';
      setError(errorMsg);
      const toast = await import('sonner');
      toast.toast.error('Network Error', {
        description: errorMsg
      });
    }
  };

  const handleUpdateBatch = async (data: Partial<BatchData>) => {
    if (editingBatch) {
      try {
        // Find the backend batch by batch number
        const backendBatch = backendBatches.find(b => b.batchNumber === editingBatch.id);

        if (!backendBatch) {
          const errorMsg = 'Batch not found in backend. Please ensure the batch exists and try again.';
          setError(errorMsg);
          const toast = await import('sonner');
          toast.toast.error('Update Failed', {
            description: errorMsg
          });
          return;
        }

        // Use externalId (batchId) for the update API call
        const batchId = backendBatch.externalId || backendBatch.batchNumber;

        // Helper to map frontend status to backend status
        const mapStatusToBackend = (status: BatchStatus): 'SCHEDULED' | 'IN_PRODUCTION' | 'COMPLETED' | 'QUALITY_TESTED' | 'READY_FOR_SALE' => {
          if (['SCHEDULED', 'IN_PRODUCTION', 'COMPLETED', 'QUALITY_TESTED', 'READY_FOR_SALE'].includes(status)) {
            return status as 'SCHEDULED' | 'IN_PRODUCTION' | 'COMPLETED' | 'QUALITY_TESTED' | 'READY_FOR_SALE';
          }
          switch (status) {
            case 'testing': return 'QUALITY_TESTED';
            case 'processing': return 'IN_PRODUCTION';
            case 'transferred': return 'COMPLETED';
            case 'ready': return 'READY_FOR_SALE';
            default: return 'SCHEDULED';
          }
        };

        // Prepare the update request with all editable fields
        const request: {
          notes?: string;
          operatorUserId?: string;
          batchStatus?: 'SCHEDULED' | 'IN_PRODUCTION' | 'COMPLETED' | 'QUALITY_TESTED' | 'READY_FOR_SALE';
        } = {};

        // Only include fields that have been changed or are provided
        if (data.notes !== undefined) {
          request.notes = data.notes || '';
        }
        
        if (data.operatorUserId !== undefined) {
          request.operatorUserId = data.operatorUserId || undefined;
        }
        
        if (data.batchStatus !== undefined) {
          request.batchStatus = mapStatusToBackend(data.batchStatus);
        }

        console.log(`📤 [BatchManagement] Updating batch ${batchId}:`, {
          notes: request.notes ? 'updated' : 'unchanged',
          operatorUserId: request.operatorUserId ? 'updated' : 'unchanged',
          batchStatus: request.batchStatus ? request.batchStatus : 'unchanged'
        });

        const response = await biogasService.updateBatch(batchId, request);

        if (response.success) {
          // Refresh batches - trigger re-fetch by incrementing refresh counter
          setRefreshTrigger(prev => prev + 1);

          const toast = await import('sonner');
          toast.toast.success('Batch Updated', {
            description: `Batch ${editingBatch.id} has been updated successfully`
          });
          setEditingBatch(null);
          setError(null);
        } else {
          const errorMsg = response.error || 'Failed to update batch';
          setError(errorMsg);
          const toast = await import('sonner');
          toast.toast.error('Failed to Update Batch', {
            description: errorMsg
          });
        }
      } catch (err) {
        console.error('Error updating batch:', err);
        const errorMsg = 'Network error while updating batch';
        setError(errorMsg);
        const toast = await import('sonner');
        toast.toast.error('Network Error', {
          description: errorMsg
        });
      }
    }
  };

  const handleDeleteBatch = async () => {
    if (deletingBatch) {
      try {
        // Call the API to delete the batch
        await api.deleteBatch(deletingBatch.id);

        // ✅ Find the backend batch to get the associated dung collection IDs
        const backendBatch = backendBatches.find(b => b.batchNumber === deletingBatch.id);

        // ✅ Immediately remove the batch from the UI
        setBackendBatches(prev => prev.filter(b => b.batchNumber !== deletingBatch.id));

        // ✅ Restore the transactions from the deleted batch back to the dropdown (mark as unassigned)
        if (backendBatch?.dungCollectionIds && Array.isArray(backendBatch.dungCollectionIds)) {
          setDungCollections(prev => prev.map(collection => {
            const collectionIdStr = collection.id?.toString() || '';
            // Check if this collection was in the deleted batch
            const wasInDeletedBatch = backendBatch.dungCollectionIds.some(
              id => id?.toString() === collectionIdStr
            );
            return {
              ...collection,
              assignedToBatch: wasInDeletedBatch ? false : collection.assignedToBatch
            };
          }));
        }

        // Close the delete dialog
        setDeletingBatch(null);

        // Show success toast
        const toast = await import('sonner');
        toast.toast.success('Batch Deleted Successfully', {
          description: `Batch ${deletingBatch.id} has been removed and its transactions are available for new batches.`
        });
      } catch (error) {
        console.error('Failed to delete batch:', error);
        const toast = await import('sonner');
        toast.toast.error('Failed to Delete Batch', {
          description: error instanceof Error ? error.message : 'An error occurred while deleting the batch'
        });
      }
    }
  };

  // ✅ Function to refresh dung collections list (for auto-refresh after batch creation)
  const refreshDungCollections = async () => {
    try {
      const response = await biogasService.listDungCollections(
        undefined, // clusterId
        undefined, // gaushalaId
        undefined, // paymentStatus
        undefined, // startDate
        undefined, // endDate
        0, // page
        100 // size
      );
      if (response.success && response.data) {
        setDungCollections(response.data.content || []);
      }
    } catch (error) {
      console.error('Error refreshing dung collections:', error);
    }
  };

  const activeBatches = batchesToDisplay.filter(b => b.status === 'processing' || b.status === 'testing' || b.status === 'IN_PRODUCTION');
  const readyBatches = batchesToDisplay.filter(b => b.status === 'ready' || b.status === 'READY_FOR_SALE');
  const transferredBatches = batchesToDisplay.filter(b => b.status === 'transferred' || b.status === 'COMPLETED');
  const totalVolume = batchesToDisplay.reduce((sum, b) => sum + b.volume, 0);
  const averageQuality = batchesToDisplay.filter(b => b.qualityScore).reduce((sum, b) => sum + (b.qualityScore || 0), 0) / batchesToDisplay.filter(b => b.qualityScore).length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
    <div className="flex items-center justify-between mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200" style={{ marginTop: '60px' }}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4" />
              {t('createBatch')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[525px] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>{t('createBatch')}</DialogTitle>
              <DialogDescription>
                Create a new biogas batch from digester output with detailed source information
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 px-1">
              <BatchForm
                onSubmit={handleCreateBatch}
                onCancel={() => setIsCreateDialogOpen(false)}
                onRefreshDungCollections={refreshDungCollections} // ✅ Pass refresh callback
                t={t}
              />
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Enhanced Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Batches</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeBatches.length}</div>
            <p className="text-xs text-muted-foreground">
              Currently processing
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ready for Transfer</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{readyBatches.length}</div>
            <p className="text-xs text-muted-foreground">
              Quality approved
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Volume</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalVolume.toFixed(1)} m³</div>
            <p className="text-xs text-muted-foreground">
              All batches combined
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Quality</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageQuality.toFixed(1)}/100</div>
            <p className="text-xs text-muted-foreground">
              Quality score average
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Certificates</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{certificates.length}</div>
            <p className="text-xs text-muted-foreground">
              Available for download
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="batches" className="space-y-6">
        <TabsList>
          <TabsTrigger value="batches">{t('batchList')}</TabsTrigger>
          <TabsTrigger value="quality">{t('activeQualities')}</TabsTrigger>
          <TabsTrigger value="certificates">{t('certificates')}</TabsTrigger>
        </TabsList>

        <TabsContent value="batches">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('batchList')}</CardTitle>
                  <CardDescription>
                    Manage all biogas batches and their lifecycle with detailed source tracking
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select value={selectedClusterId || 'all'} onValueChange={setSelectedClusterId} disabled={loadingClusters}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder={loadingClusters ? "Loading clusters..." : "Select cluster"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clusters</SelectItem>
                      {userClusters.map((cluster) => (
                        <SelectItem key={cluster.id} value={cluster.id.toString()}>
                          {cluster.clusterName} ({cluster.clusterCode})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder={t('search')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as BatchStatus | 'all')}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="testing">Testing</SelectItem>
                      <SelectItem value="ready">Ready</SelectItem>
                      <SelectItem value="transferred">Transferred</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('batchId')}</TableHead>
                    <TableHead>{t('farmerName')}</TableHead>
                    <TableHead>{t('source')}</TableHead>
                    <TableHead>{t('volume')}</TableHead>
                    <TableHead>{t('methaneContent')}</TableHead>
                    <TableHead>{t('status')}</TableHead>
                    <TableHead>{t('qualityScore')}</TableHead>
                    <TableHead>{t('createdDate')}</TableHead>
                    <TableHead>{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBatches.map((batch) => (
                    <TableRow key={batch.id}>
                      <TableCell className="font-medium">{batch.id}</TableCell>
                      <TableCell>{batch.farmerName || 'N/A'}</TableCell>
                      <TableCell className="max-w-32 truncate" title={batch.source}>
                        {batch.source || 'N/A'}
                      </TableCell>
                      <TableCell>{batch.volume.toFixed(1)} m³</TableCell>
                      <TableCell>{batch.methaneContent.toFixed(1)}%</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(batch.status)}>
                          <div className="flex items-center gap-1">
                            {getStatusIcon(batch.status)}
                            {t(batch.status)}
                          </div>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {batch.qualityScore ? (
                          <span className={`font-medium ${
                            batch.qualityScore >= 90 ? 'text-green-600' :
                            batch.qualityScore >= 70 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {batch.qualityScore}/100
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Pending</span>
                        )}
                      </TableCell>
                      <TableCell>{new Date(batch.createdDate).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingBatch(batch)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDeletingBatch(batch)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewingBatch(batch)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quality">
          <Card>
            <CardHeader>
              <CardTitle>{t('activeQualities')}</CardTitle>
              <CardDescription>
                Monitor ongoing quality testing processes with detailed progress tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {batches.filter(b => b.status === 'testing').map((batch) => (
                  <div key={batch.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <h4 className="font-semibold">{batch.id}</h4>
                      <p className="text-sm text-muted-foreground">
                        Farmer: {batch.farmerName} | Volume: {batch.volume.toFixed(1)} m³
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Source: {batch.source}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge className={getStatusColor(batch.status)}>
                        {t('testing')}
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => setViewingProgress(batch)}>
                        View Progress
                      </Button>
                    </div>
                  </div>
                ))}
                {batches.filter(b => b.status === 'testing').length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-2" />
                    <p>No active quality tests</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certificates">
          <Card>
            <CardHeader>
              <CardTitle>{t('certificates')}</CardTitle>
              <CardDescription>
                Download and manage quality certificates with detailed certification information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {certificates.map((cert) => {
                  const batch = batches.find(b => b.id === cert.batchId);
                  return (
                    <div key={cert.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-1">
                        <h4 className="font-semibold">{cert.batchId}</h4>
                        <p className="text-sm text-muted-foreground">
                          Farmer: {batch?.farmerName} | Score: {cert.qualityScore}/100
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Certified by: {cert.certifiedBy}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Issued: {new Date(cert.issuedDate).toLocaleDateString()} |
                          Valid until: {cert.validUntil ? new Date(cert.validUntil).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge className="bg-green-100 text-green-800">
                          Certified
                        </Badge>
                        <Button size="sm" variant="outline">
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {certificates.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-2" />
                    <p>No certificates available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editingBatch} onOpenChange={() => setEditingBatch(null)}>
        <DialogContent className="sm:max-w-[525px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('editBatch')}</DialogTitle>
            <DialogDescription>
              Update batch information and status with detailed source tracking
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-1">
            {editingBatch && (
              <BatchForm
                batch={editingBatch}
                onSubmit={handleUpdateBatch}
                onCancel={() => setEditingBatch(null)}
                t={t}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deletingBatch} onOpenChange={() => setDeletingBatch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deleteBatch')}</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete batch {deletingBatch?.id}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingBatch(null)}>
              {t('cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteBatch}>
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={!!viewingBatch} onOpenChange={() => setViewingBatch(null)}>
        <DialogContent className="sm:max-w-[625px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('batchDetails')}</DialogTitle>
            <DialogDescription>
              Complete information about batch {viewingBatch?.id}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-1">
            {viewingBatch && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('batchId')}</Label>
                    <p className="text-base font-semibold">{viewingBatch.id}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('digesterId')}</Label>
                    <p className="text-base">{viewingBatch.digesterId}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('farmerName')}</Label>
                    <p className="text-base">{viewingBatch.farmerName || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('source')}</Label>
                    <p className="text-base">{viewingBatch.source || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('volume')}</Label>
                    <p className="text-base">{viewingBatch.volume.toFixed(1)} m³</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('methaneContent')}</Label>
                    <p className="text-base">{viewingBatch.methaneContent.toFixed(1)}%</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('status')}</Label>
                    <Badge className={getStatusColor(viewingBatch.status)}>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(viewingBatch.status)}
                        {t(viewingBatch.status)}
                      </div>
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('qualityScore')}</Label>
                    <p className={`text-base font-semibold ${
                      viewingBatch.qualityScore && viewingBatch.qualityScore >= 90 ? 'text-green-600' :
                      viewingBatch.qualityScore && viewingBatch.qualityScore >= 70 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {viewingBatch.qualityScore ? `${viewingBatch.qualityScore}/100` : 'Pending'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-500">{t('createdDate')}</Label>
                    <p className="text-base">{new Date(viewingBatch.createdDate).toLocaleString()}</p>
                  </div>
                  {viewingBatch.transferDate && (
                    <div>
                      <Label className="text-sm font-medium text-gray-500">{t('transferDate')}</Label>
                      <p className="text-base">{new Date(viewingBatch.transferDate).toLocaleString()}</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-500">{t('notes')}</Label>
                  <p className="text-sm mt-1 p-3 bg-gray-50 rounded-md">{viewingBatch.notes || 'No notes available'}</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingBatch(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Progress Dialog */}
      <Dialog open={!!viewingProgress} onOpenChange={() => setViewingProgress(null)}>
        <DialogContent className="sm:max-w-[625px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Quality Test Progress</DialogTitle>
            <DialogDescription>
              Real-time progress tracking for batch {viewingProgress?.id}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 px-1">
            {viewingProgress && (
              <div className="space-y-6">
                {/* Overall Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <Label className="font-medium">Overall Progress</Label>
                    <span className="text-gray-600">
                      {viewingProgress.qualityScore ? Math.round(viewingProgress.qualityScore) : 0}%
                    </span>
                  </div>
                  <Progress value={viewingProgress.qualityScore || 0} className="h-3" />
                </div>

                {/* Test Stages */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium text-gray-700">Test Stages</Label>

                  {/* Methane Content Analysis */}
                  <div className="space-y-2 p-3 bg-gray-50 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Methane Content Analysis</span>
                      <Badge variant={viewingProgress.methaneContent ? "success" : "secondary"}>
                        {viewingProgress.methaneContent ? "Completed" : "Pending"}
                      </Badge>
                    </div>
                    {viewingProgress.methaneContent && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Methane %</span>
                          <span className="font-medium">{viewingProgress.methaneContent}%</span>
                        </div>
                        <Progress value={viewingProgress.methaneContent} className="h-2" />
                      </div>
                    )}
                  </div>

                  {/* Volume Measurement */}
                  <div className="space-y-2 p-3 bg-gray-50 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Volume Measurement</span>
                      <Badge variant={viewingProgress.volumeProduced ? "success" : "secondary"}>
                        {viewingProgress.volumeProduced ? "Completed" : "Pending"}
                      </Badge>
                    </div>
                    {viewingProgress.volumeProduced && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Volume Produced</span>
                          <span className="font-medium">{viewingProgress.volumeProduced} m³</span>
                        </div>
                        <Progress value={100} className="h-2" />
                      </div>
                    )}
                  </div>

                  {/* Quality Score */}
                  <div className="space-y-2 p-3 bg-gray-50 rounded-md">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Quality Assessment</span>
                      <Badge variant={viewingProgress.qualityScore ? "success" : "secondary"}>
                        {viewingProgress.qualityScore ? "Completed" : "In Progress"}
                      </Badge>
                    </div>
                    {viewingProgress.qualityScore && (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Quality Score</span>
                          <span className="font-medium">{viewingProgress.qualityScore.toFixed(1)}/100</span>
                        </div>
                        <Progress value={viewingProgress.qualityScore} className="h-2" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Batch Information */}
                <div className="space-y-3 border-t pt-4">
                  <Label className="text-sm font-medium text-gray-700">Batch Information</Label>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Batch ID:</span>
                      <p className="font-medium mt-1">{viewingProgress.id}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <p className="font-medium mt-1">
                        <Badge variant={
                          viewingProgress.status === 'COMPLETED' ? 'success' :
                          viewingProgress.status === 'IN_PRODUCTION' ? 'default' :
                          'secondary'
                        }>
                          {viewingProgress.status}
                        </Badge>
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Started:</span>
                      <p className="font-medium mt-1">
                        {new Date(viewingProgress.startDate).toLocaleDateString()}
                      </p>
                    </div>
                    {viewingProgress.endDate && (
                      <div>
                        <span className="text-gray-500">Completed:</span>
                        <p className="font-medium mt-1">
                          {new Date(viewingProgress.endDate).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {viewingProgress.notes && (
                  <div className="space-y-2 border-t pt-4">
                    <Label className="text-sm font-medium text-gray-700">Test Notes</Label>
                    <p className="text-sm p-3 bg-gray-50 rounded-md text-gray-600">
                      {viewingProgress.notes}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingProgress(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BatchManagement;